import { assignInlineVars } from '@vanilla-extract/dynamic';
import { cx } from 'emotion';
import {
  CSSProperties,
  ComponentType,
  Suspense,
  lazy,
  useEffect,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';

import { WinnerList } from '@ezugi/components';
import { LOADING, GAME_RESULT, PLACE_YOUR_BETS } from '@ezugi/constants';
import { useOrientationChange } from '@ezugi/hooks/src/useOrientationChange';
import { ConditionalPortalWrapped } from '@ezugi/primitives';
import { GenericStateType } from '@ezugi/types';
import { DeviceInfo, ss } from '@ezugi/utils';

import WinnerListV3 from '../../V3/components/WinnerList/WinnerList';
import { WINNER_LIST_INDEX } from '../../V3/components/WinnerList/constants';
import { useAmount } from '../../hooks/useAmount';
import { useIntl } from '../../hooks/useIntl';
import {
  boardToggledSelector,
  gameResultsConfigSelector,
  roundSelector,
  sortedWinnerListSelector,
  totalWinningsAmountSelector,
  winnerListConfigSelector,
  desktopBoardToggleHeightSelector,
} from '../../store/selectors';
import {
  fixedContainer, gameResultsContainer,
  gameResultsContainerMobile, gameResultsTopSpace, mobileWinnerListSingleRowClassName,
  mobileWinnersListSingleRowV3Classname,
} from './GameResults.css';
import { type ResultMessageProps } from './ResultMessage';

const ResultMessageBootstrap = lazy(() => import('./ResultMessage'));

type GameResultsProps = {
  className?: string;
  hideWinnerList?: boolean;
  components?: {
    ResultMessage?: ComponentType<ResultMessageProps>;
  };
  winnerListOptions?: WinnerListProps;
};

export type WinnerListProps = {
  className?: string;
  isWinnerClassName?: string; // used only in andar bahar
  showCurrencySymbol?: boolean;
  showCurrencyCode?: boolean;
  mobileTarget?: string;
  style?: CSSProperties;
  maxLength?: number;
  extraAllowedStates?: string[];
  useV3WinnerList?: boolean;
  isMobileMultiRow?: boolean;
};

const intlKeysWinnerList = {
  totalWinningsLabel: 'general.total_winnings',
};

const ConnectedWinnerList = ({
  className,
  isWinnerClassName,
  useV3WinnerList = false,
  mobileTarget,
  style,
  maxLength,
  isMobileMultiRow,
  extraAllowedStates = [],
}: WinnerListProps) => {
  const BaseWinnerList = useV3WinnerList ? WinnerListV3 : WinnerList;

  const round = useSelector(roundSelector);
  const winnerListConfig = useSelector(winnerListConfigSelector);
  const winnerList = useSelector(sortedWinnerListSelector);

  const totalWinningsAmount = useSelector(totalWinningsAmountSelector);

  // winner list can be disabled from the game config
  // don't show winner list when there are no winners, or
  // the game engine is not in a correct state
  // LOADING is needed to keep the winner list during the move table flow
  const showWinnerList = winnerList.length > 0
    && [ LOADING, GAME_RESULT, PLACE_YOUR_BETS, ...extraAllowedStates ].includes(
      round.roundStatus
    );

  const { totalWinningsLabel } = useIntl({
    keys: intlKeysWinnerList,
  });

  const shouldRoundTotalWinnings = totalWinningsAmount > 1
    && winnerListConfig.roundAmount
    && DeviceInfo.isHandheld;

  const { totalWinningsAmountDisplay } = useAmount({
    values: {
      totalWinningsAmountDisplay: {
        value: shouldRoundTotalWinnings
          ? Math.round(totalWinningsAmount)
          : totalWinningsAmount,
        type: 'bet',
        showCurrencySymbol: winnerListConfig.showCurrencySymbol,
        showCurrencyCode: winnerListConfig.showCurrencyCode,
      },
    },
  });

  useEffect(() => {
    ss.del(WINNER_LIST_INDEX);
  }, []);

  if (!showWinnerList) {
    return null;
  }

  return (
    <ConditionalPortalWrapped
      target={mobileTarget}
      shouldUsePortal={DeviceInfo.isHandheld && useV3WinnerList}
    >
      <BaseWinnerList
        data-e2e="result-message-winner-list"
        style={style}
        maxLength={maxLength}
        isMobileMultiRow={isMobileMultiRow}
        list={winnerList}
        header={{
          label: totalWinningsLabel,
          amount: totalWinningsAmountDisplay,
        }}
        duration={winnerListConfig.duration}
        className={cx(
          {
            [mobileWinnerListSingleRowClassName]:
              DeviceInfo.isHandheld && !isMobileMultiRow,
            [mobileWinnersListSingleRowV3Classname]:
              DeviceInfo.isHandheld && !isMobileMultiRow && useV3WinnerList,
          },
          className,
          (round.winAmount ?? 0) > 0 ? isWinnerClassName : undefined
        )}
      />
    </ConditionalPortalWrapped>
  );
};

const GameResults = ({
  className,
  hideWinnerList,
  components,
  winnerListOptions = {},
}: GameResultsProps) => {
  const toggled = useSelector(boardToggledSelector);
  const round = useSelector(roundSelector);

  const gameResultsConfig = useSelector(
    gameResultsConfigSelector
  ) as unknown as GenericStateType;

  const desktopBoardToggleHeight = useSelector(
    desktopBoardToggleHeightSelector,
  );
  const gameResultsStyle = !DeviceInfo.isHandheld
    ? { height: `${100 - desktopBoardToggleHeight}%` }
    : {};

  const orientation = useOrientationChange();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isSmallWindow = useMemo(() => window.innerHeight < 620, [ orientation ]);

  const showResultMessage = round.roundStatus === GAME_RESULT; // true

  const ResultMessage = components?.ResultMessage ?? ResultMessageBootstrap;

  return (
    <div
      data-e2e="game-results"
      className={cx(
        gameResultsContainer,
        {
          [fixedContainer]: toggled || isSmallWindow,
          [gameResultsContainerMobile]: DeviceInfo.isHandheld,
        },
        className
      )}
      style={{
        ...gameResultsStyle,
        ...assignInlineVars({
          [gameResultsTopSpace]: DeviceInfo.isHandheld
            ? gameResultsConfig.topSpacePortrait
            : gameResultsConfig.topSpaceDesktop,
        }),
      }}
    >
      <Suspense fallback={null}>
        {showResultMessage && (
          <ResultMessage
            data-e2e="result-message"
          />
        )}
      </Suspense>
      {hideWinnerList ? null : <ConnectedWinnerList {...winnerListOptions} />}
    </div>
  );
};

export default GameResults;
